import React, { useCallback, useRef, useState, useEffect } from "react";
import * as Scrivito from "scrivito";
import { Form } from "reactstrap";
import { getHomepage, getPageLink } from "utils";
import { UserNameInput } from "components/AuthInputs/UserNameInput";
import { PasswordInput } from "components/AuthInputs/PasswordInput";
import { Button, Icon } from "components";
import {
  useUser,
  useUserActions,
  useIsUserLoading,
  useUserError,
} from "stores/user";
import { useToastCleanup, useTranslate } from "hooks";
import { useIsSameProj } from "stores/globalStore";
import type { Id } from "react-toastify";

type LoginViewProps = {
  page: Scrivito.Obj;
};

const LoginView = Scrivito.connect(({ page }: LoginViewProps) => {
  const translate = useTranslate();
  const isSameProj = useIsSameProj();
  const user = useUser();
  const userActions = useUserActions();

  useEffect(() => {
    if (user?.id) {
      userActions.redirectToWelcomePage();
    }
  }, [user, userActions]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [hasLoginBeenAttempted, setHasLoginBeenAttempted] = useState(false);
  const toastRef = useRef<Id | undefined>(undefined);
  const isUserLoginPending = useIsUserLoading();
  const error = useUserError();
  const loginError = hasLoginBeenAttempted ? error : null;

  const homepage = getHomepage();

  useToastCleanup(toastRef);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
    },
    [],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
    },
    [],
  );

  const handleLogin = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setHasLoginBeenAttempted(true);
      try {
        await userActions.login(email, password);
      } catch (err) {
        console.error(err);
      }
    },
    [userActions, email, password],
  );

  if (!homepage) {
    return null;
  }

  return (
    <>
      {!isSameProj && (
        <div className="bg-white mx-auto col-md-9 col-xl-6 p-4 mb-7 font-md text-center">
          <h4 id="beta" className="text-blue-300 mx-auto">
            BETA
          </h4>
          {translate("TROX_BETA_INFO_TEXT")}
        </div>
      )}
      <Form
        className="bg-white col-md-9 col-xl-6 mx-auto p-0 d-flex flex-column font-md"
        data-testid="login-view"
      >
        <div className="p-4">
          <Scrivito.LinkTag
            data-testid="LoginView_back-home-page-link"
            to={getPageLink(homepage)}
            className="text-secondary"
          >
            <Icon iconName="chevron-left-regular" className="me-1" />
            <span>{translate("LOGIN_VIEW_BACK_TO_TROX_HOMEPAGE")}</span>
          </Scrivito.LinkTag>
        </div>
        <div className="px-4 mx-auto text-center">
          <Icon iconName="user-light" className="icon-96 text-blue-800" />
          <h6 className="mb-5_5">{translate("LOGIN_VIEW_LOGIN_HEADER")}</h6>
          <UserNameInput
            value={email}
            label={translate("LOGIN_VIEW_EMAIL_FIELD_LABEL")}
            error={loginError}
            name="userEmailField"
            id="userEmailFieldId"
            type="email"
            testId="email"
            onChange={handleEmailChange}
          />
          <PasswordInput
            classNames="mb-2"
            value={password}
            error={loginError}
            name="passwordField"
            id="passwordFieldId"
            testId="password"
            onChange={handlePasswordChange}
            label={translate("LOGIN_VIEW_PASSWORD_FIELD_LABEL")}
          />
          <div className="row m-0">
            {loginError && (
              <div className="col-md-7 p-0 text-start text-danger">
                {translate("LOGIN_VIEW_ERROR_OCCURRED")}
              </div>
            )}
            <div className={`col-md-${loginError ? 5 : 16} text-end p-0`}>
              <Scrivito.LinkTag
                data-testid="LoginView_reset-pass-link"
                to={page}
                params={{ view: "forgotPass" }}
              >
                {translate("LOGIN_VIEW_LOGIN_RESET")}
              </Scrivito.LinkTag>
            </div>
          </div>
          <div className="text-end mt-5 mb-6 d-flex gap-4 justify-content-end align-items-center">
            <Button
              data-testid="login-view_new-account-btn"
              color="secondary"
              onClick={() => {
                Scrivito.navigateTo(page, { view: "newAccount" });
              }}
              disabled={isUserLoginPending}
            >
              {translate("LOGIN_VIEW_CREATE_NEW_ACCOUNT")}
            </Button>
            <Button
              data-testid="login-view_login-btn"
              disabled={isUserLoginPending}
              color="primary"
              type="submit"
              onClick={handleLogin}
            >
              {translate("LOGIN_VIEW_LOGIN_BUTTON")}
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
});

export default LoginView;
